import React, { useState } from "react";

import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
} from "@mui/material";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { createEspisodeService, RssFeedEpisode, RssFeed } from "../../services";
import ButtonView from "../../../common/Button";
import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../../utils/formatEpisodeLengthToHumanFriendlyDuration";

export interface RssEpisodeSelection {
  episode: RssFeedEpisode;
  podcastTitle: string;
}

interface RssImportProps {
  selectedEpisode: RssEpisodeSelection | null;
  onEpisodeSelect: (selection: RssEpisodeSelection | null) => void;
}

export default function RssImport({
  selectedEpisode,
  onEpisodeSelect,
}: RssImportProps) {
  const [feedUrl, setFeedUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [feed, setFeed] = useState<RssFeed | null>(null);
  const [episodeSearch, setEpisodeSearch] = useState<string>("");

  const handleLoad = async () => {
    if (!feedUrl.trim()) return;
    setLoading(true);
    setError("");
    setFeed(null);
    onEpisodeSelect(null);
    try {
      const result = await createEspisodeService.getRssFeedEpisodes(
        feedUrl.trim(),
      );
      if (result.episodes.length === 0) {
        setError("No episodes found in this RSS feed.");
        return;
      }
      setFeed(result);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not load RSS feed. Check the URL and try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFeedUrl("");
    setFeed(null);
    setError("");
    setEpisodeSearch("");
    onEpisodeSelect(null);
  };

  const handleSelectEpisode = (episode: RssFeedEpisode) => {
    onEpisodeSelect({ episode, podcastTitle: feed?.podcast_title ?? "" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLoad();
  };

  const filteredEpisodes = feed?.episodes.filter((ep) =>
    ep.title.toLowerCase().includes(episodeSearch.toLowerCase()),
  );

  if (selectedEpisode) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            color: "#23272E",
            fontWeight: 700,
            fontSize: "1.125rem",
          }}
        >
          Episode selected
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Review the episode, then continue to episode details.
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            {selectedEpisode.episode.image_url ? (
              <Box
                component="img"
                src={selectedEpisode.episode.image_url}
                alt="Episode artwork"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "8px",
                  backgroundColor: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AudioFileIcon sx={{ color: "#2563eb", fontSize: 28 }} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#23272E",
                  wordBreak: "break-word",
                }}
              >
                {selectedEpisode.episode.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {selectedEpisode.podcastTitle}
                {selectedEpisode.episode.duration_in_seconds
                  ? ` · ${formatEpisodeLengthToHumanFriendlyDuration(selectedEpisode.episode.duration_in_seconds)}`
                  : ""}
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Chip
                  icon={
                    <CheckCircleIcon sx={{ fontSize: "18px !important" }} />
                  }
                  label="Ready to continue"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(22, 101, 52, 0.08)",
                    color: "#15803d",
                    fontWeight: 600,
                    borderRadius: "8px",
                    "& .MuiChip-icon": { color: "#15803d" },
                  }}
                />
              </Box>
            </Box>
            <ButtonView
              variant="outlined"
              label="Change"
              type="button"
              handleOnClick={handleClear}
              sx={{
                borderRadius: "8px",
                px: 2,
                fontSize: "12px",
                fontWeight: 600,
                height: 32,
                textTransform: "none",
                flexShrink: 0,
                mt: -0.5,
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": { borderColor: "#2563eb", color: "#2563eb" },
              }}
            />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {!feed ? (
        <Paper
          elevation={0}
          sx={{
            border: "2px dashed",
            borderColor: "#2563eb",
            borderRadius: "12px",
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            backgroundColor: "#eff6ff",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              backgroundColor: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <RssFeedIcon sx={{ fontSize: 32, color: "#fff" }} />
          </Box>
          <Typography
            sx={{
              mb: 0.75,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "15px",
            }}
          >
            Import from RSS Feed
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 2, fontSize: "13px" }}>
            Paste your podcast RSS feed URL to browse and import episodes
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              mb: 3,
              px: 1.5,
              py: 0.75,
              borderRadius: "8px",
              backgroundColor: "#dbeafe",
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 14, color: "#2563eb", flexShrink: 0 }}
            />
            <Typography
              sx={{ color: "#1d4ed8", fontSize: "12px", fontWeight: 600 }}
            >
              For video podcasts, only audio will be extracted
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              maxWidth: 520,
              mx: "auto",
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="https://feeds.example.com/podcast.xml"
              value={feedUrl}
              onChange={(e) => {
                setFeedUrl(e.target.value);
                setError("");
              }}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: "#fff",
                },
              }}
            />
            <ButtonView
              variant="contained"
              label={loading ? "" : "Load"}
              type="button"
              handleOnClick={handleLoad}
              disabled={loading || !feedUrl.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                backgroundColor: "#2563eb",
                borderRadius: "8px",
                px: 2.5,
                fontSize: "13px",
                fontWeight: 600,
                height: 40,
                textTransform: "none",
                flexShrink: 0,
                minWidth: 90,
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            />
          </Box>

          {error && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 2,
                p: 1.5,
                backgroundColor: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                textAlign: "left",
                maxWidth: 520,
                mx: "auto",
              }}
            >
              <ErrorOutlineIcon
                sx={{ color: "#dc2626", fontSize: 16, flexShrink: 0 }}
              />
              <Typography
                sx={{ color: "#dc2626", fontWeight: 500, fontSize: "12px" }}
              >
                {error}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1rem", color: "#23272E" }}
              >
                {feed.podcast_title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {feed.episodes.length} episode
                {feed.episodes.length !== 1 ? "s" : ""} · Select one to import
              </Typography>
            </Box>
            <ButtonView
              variant="outlined"
              label="Change feed"
              type="button"
              handleOnClick={handleClear}
              sx={{
                borderRadius: "8px",
                px: 2,
                fontSize: "12px",
                fontWeight: 600,
                height: 32,
                textTransform: "none",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": { borderColor: "#2563eb", color: "#2563eb" },
              }}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Search episodes..."
            value={episodeSearch}
            onChange={(e) => setEpisodeSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
              },
            }}
          />

          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "12px",
              maxHeight: 360,
              overflow: "auto",
            }}
          >
            <List disablePadding>
              {filteredEpisodes?.map((ep, idx) => (
                <ListItemButton
                  key={ep.guid ?? ep.enclosure_url ?? idx}
                  onClick={() => handleSelectEpisode(ep)}
                  divider={idx < (filteredEpisodes?.length ?? 0) - 1}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": { backgroundColor: "#eff6ff" },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={ep.image_url}
                      variant="rounded"
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "8px",
                        bgcolor: "#eff6ff",
                      }}
                    >
                      <AudioFileIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#23272E",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ep.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{ fontSize: "12px", color: "text.secondary" }}
                      >
                        {ep.pub_date
                          ? new Date(ep.pub_date).toLocaleDateString()
                          : ""}
                        {ep.duration_in_seconds
                          ? `${ep.pub_date ? " · " : ""}${formatEpisodeLengthToHumanFriendlyDuration(ep.duration_in_seconds)}`
                          : ""}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
              {filteredEpisodes?.length === 0 && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "13px" }}
                  >
                    No episodes match your search.
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
